import React, { useEffect, useState } from "react";
import ModelIcon from "./ModelIcon";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import "./Dashboard.css";
import { useAuth } from "../../context/AuthContext";

const AVAILABLE_MODELS = ["ChatGPT", "Claude", "Gemini", "Perplexity", "Grok"];

// Define which models require a paid account
const PREMIUM_MODELS = ["Grok", "Perplexity"];

export default function ModelSelector({
  selectedModels = [],
  onToggleModel,
  disabled = false,
  maxModels = 4,
  variants = null,
  allowedModels = null, // Used if we want to completely hide models (not used for free tier logic where we show them locked)
}) {
  const { isAuthenticated } = useAuth();
  const [isPhoneScreen, setIsPhoneScreen] = useState(window.innerWidth <= 768);
  const itemVariants = variants?.item || {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };
  const containerVariants = variants?.container || {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  const visibleModels = allowedModels
    ? AVAILABLE_MODELS.filter((m) => allowedModels.includes(m))
    : AVAILABLE_MODELS;

  useEffect(() => {
    const handleResize = () => setIsPhoneScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getTooltipAlign = (modelName) => {
    if (isPhoneScreen) {
      if (modelName === "ChatGPT" || modelName === "Perplexity") return "left";
      if (modelName === "Claude") return "center";
      if (modelName === "Gemini" || modelName === "Grok") return "right";
    }
    return modelName === "Grok" || modelName === "Claude" || modelName === "Gemini"
      ? "center"
      : ([0, 1, 3].includes(AVAILABLE_MODELS.indexOf(modelName)) ? "left" : "right");
  };

  const renderModelButton = (model) => {
    const isSelected = selectedModels.includes(model);
    const isPremiumLocked =
      !isAuthenticated && PREMIUM_MODELS.includes(model);
    const isDisabled =
      disabled ||
      isPremiumLocked ||
      (!isSelected && selectedModels.length >= maxModels);
    const isMaxLimited =
      !isPremiumLocked && !isSelected && selectedModels.length >= maxModels;

    return (
      <motion.button
        key={model}
        className={`model-btn ${
          isSelected ? "model-btn-active" : "model-btn-outline"
        } ${isPremiumLocked ? "model-btn-locked" : ""} ${
          isMaxLimited ? "model-btn-max-limited" : ""
        } ${
          !isAuthenticated || isMaxLimited
            ? "model-btn-no-hover-circle"
            : ""
        }`}
        onClick={() => {
          if (isPremiumLocked) return;
          onToggleModel && onToggleModel(model);
        }}
        variants={itemVariants}
        whileHover={isDisabled ? {} : { scale: 1.05, y: -2 }}
        whileTap={isDisabled ? {} : { scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
        disabled={isDisabled && !isPremiumLocked}
        style={{
          cursor:
            isDisabled && !isPremiumLocked && !isSelected
              ? "not-allowed"
              : "pointer",
          filter:
            isDisabled && !isPremiumLocked && !isSelected
              ? "grayscale(0.15) brightness(0.92)"
              : "none",
          position: "relative",
        }}
      >
        {isPremiumLocked && <Lock size={14} className="lock-icon" />}
        <ModelIcon model={model} size={15} className="model-btn-icon" />
        {model}

        {isPremiumLocked && (
          <div
            className="locked-tooltip"
            data-align={getTooltipAlign(model)}
          >
            <Lock
              size={12}
              style={{
                display: "inline",
                marginRight: "4px",
                verticalAlign: "text-bottom",
              }}
            />
            Available on <Link to="/pricing">Free Trial</Link> <br /> (up to 4 AI
            models)
          </div>
        )}

        {!isAuthenticated && isMaxLimited && (
          <div
            className="locked-tooltip model-limit-tooltip"
            data-align={getTooltipAlign(model)}
          >
            A maximum of {maxModels} models can be selected.
            <br />
            Start a <Link to="/pricing" style={{ color: "#ffffff", textDecoration: "underline", fontWeight: 600 }}>Free Trial</Link> to select up to 4 models.
          </div>
        )}
      </motion.button>
    );
  };

  return (
    <>
      <motion.p className="model-select-label" variants={itemVariants}>
        {!isAuthenticated
          ? "Free Access - Compare up to 2 of 5 AI Models"
          : `Select up to ${maxModels === 2 ? "two" : "four"} AI(s) that should perform this task:`}
      </motion.p>

      <motion.div
        className={`model-buttons ${!isAuthenticated && isPhoneScreen ? "model-buttons-anon-mobile" : ""}`}
        variants={containerVariants}
      >
        {!isAuthenticated && isPhoneScreen ? (
          <>
            <div className="model-buttons-row">
              {visibleModels.slice(0, 3).map(renderModelButton)}
            </div>
            <div className="model-buttons-row model-buttons-row-centered">
              {visibleModels.slice(3).map(renderModelButton)}
            </div>
          </>
        ) : (
          visibleModels.map(renderModelButton)
        )}
      </motion.div>
    </>
  );
}
